import { contentListItemSelectFragment, contentGroupItemSelectFragment, userFragment } from "./fragments";
import { QGLResponse } from "./types";
import { api } from "./index";
import { ApiParams, CategoryModel, MediaModel } from "../lib/models";
import configs from "../lib/configs";

export const getWatchListQuery = `
	query GetWatchList ($limit: Int, $offset: Int) {
	  watchList {
	  	isWatchList
	  	items (limit: $limit, offset: $offset) {
	  	    ...ContentGroupItemSelectFragment
	  	    __typename
	  	}
		__typename
	  }
	}
	${contentGroupItemSelectFragment}
`;

export const getBrandingQuery = `
    query GetBranding {
        branding {
            title
            description
            type
            backdropUrl
            backdropWideUrl
            movieId
        }
    }
`;

export const getDashboardQuery = `
    query GetDashboard ($limit: Int, $offset: Int, $includeCollections: Boolean) {
      dashboard {
        id
        items (limit: $limit, offset: $offset, includeCollections: $includeCollections) {
          ... on ContinueWatching {
            isContinueWatching
            name
            items {
              ...ContentItemsSelect
            }
            __typename
          }
          ... on Trending {
            isTrending
            name
            items {
              ...ContentItemsSelect
            }
            __typename
          }
          ... on Category {
            id
            name
            items {
              ...ContentItemsSelect
            }
            __typename
          }
          __typename
        }
        __typename
      }
    }
    ${contentListItemSelectFragment}
`;

export const getContentItemQuery = `
    query GetContentItem($id: String!, $contentType: String) {
      contentItem(id: $id, contentType: $contentType) {
      ... on Collection {
          id
          title
          description
          coverUrl
          backdropUrl
          releaseYear
          inWatchList
          fsk
		  languages
		  subtitles
		  resolution
          items {
            ...ContentItemsSelect
          }
          trailer {
            url
          }
          __typename
        }
        ... on TvSeries {
          id
          title
          description
          coverUrl
          backdropUrl
          inWatchList
          releaseYear
          fsk
          seasonCount
		  languages
		  subtitles
		  resolution
          seasons {
            id
            seasonNumber
            releaseYear
            episodes {
                id
                title
                description
                duration
                coverUrl
                isFree
                fsk
                isAdult
                episodeNumber
                __typename
              }
          }
          trailer {
            url
          }
          __typename
        }
        ... on Movie {
          id
          title
          description
          coverUrl
          backdropUrl
          inWatchList
          duration
          releaseYear
          isFree
          fsk
          isAdult
		  languages
		  subtitles
		  resolution
          progress {
            current
          }
          trailer {
            url
          }
          __typename
        }
      }
    }
    ${contentListItemSelectFragment}

`;

export const getStreamQuery = `
    query GetStream($id: String!) {
      stream(id: $id) {
        url
        format
        laUrl
        drm
        content {
          ... on Episode {
            progress {
              current
            }
          }
          ... on Movie {
            progress {
              current
            }
          }
        }
        subtitles {
          language
          languageName
          url
        }
      }
    }
`;

export const getContentItemById = async (id: string, contentType?: string): Promise<MediaModel> => {
    const { data }: QGLResponse = await api("graphql", {
        method: "POST",
        body: { query: getContentItemQuery, variables: { id: id, contentType } }
    });

    return data.contentItem;
};

export const getBranding = async (): Promise<CategoryModel[]> => {
    const {
        data: { branding }
    }: QGLResponse = await api("graphql", {
        method: "POST",
        body: { query: getBrandingQuery }
    });

    return branding.filter((v: any) => v.type !== "external");
};

export const getDashboard = async (offset = 0): Promise<CategoryModel[]> => {
    const {
        data: { dashboard }
    }: QGLResponse = await api("graphql", {
        method: "POST",
        body: { query: getDashboardQuery, variables: { limit: 15, offset: offset, includeCollections: true } }
    });

    return dashboard.items;
};

// Batched GraphQL query to fetch branding and dashboard in a single request
export const getBrandingAndDashboard = async (offset = 0) => {
    const { data }: QGLResponse = await api("graphql", {
        method: "POST",
        body: {
            query: `
                query GetBrandingAndDashboard ($limit: Int, $offset: Int, $includeCollections: Boolean) {
                  branding {
                    title
                    description
                    type
                    backdropUrl
                    backdropWideUrl
                    movieId
                  }
                  dashboard {
                    id
                    items (limit: $limit, offset: $offset, includeCollections: $includeCollections) {
                      ... on ContinueWatching {
                        isContinueWatching
                        name
                        items {
                          ...ContentItemsSelect
                        }
                        __typename
                      }
                      ... on Trending {
                        isTrending
                        name
                        items {
                          ...ContentItemsSelect
                        }
                        __typename
                      }
                      ... on Category {
                        id
                        name
                        items {
                          ...ContentItemsSelect
                        }
                        __typename
                      }
                      __typename
                    }
                    __typename
                  }
                }
                ${contentListItemSelectFragment}
            `,
            variables: { limit: 15, offset: offset, includeCollections: true }
        }
    });

    return {
        branding: data.branding,
        dashboard: data.dashboard.items
    };
};

export const getCategories = async (): Promise<CategoryModel[]> => {
    const {
        data: { categories }
    }: QGLResponse = await api("graphql", {
        method: "POST",
        body: {
            query: `
            query GetCategories ($limit: Int, $offset: Int, $includeCollections: Boolean) {
                categories (limit: $limit, offset: $offset) {
                id
                name
                items(includeCollections: $includeCollections) {
                   ...ContentItemsSelect
                }
                __typename
              }
              __typename
            }
            ${contentListItemSelectFragment}
        `,
            variables: { limit: 10, offset: 0, includeCollections: true }
        }
    });

    return categories.filter((cat: CategoryModel) => !!cat.items.length);
};

export const getWatchList = async () => {
    const {
        data: { watchList }
    }: QGLResponse = await api("graphql", {
        method: "POST",
        body: { query: getWatchListQuery, variables: { limit: 10, offset: 0 } }
    });

    return watchList.items;
};

export const getWatchListUncategorizedQuery = `
	query GetWatchListUncategorized ($limit: Int, $offset: Int) {
	  watchList {
	  	isWatchList
	  	itemsUncategorized (limit: $limit, offset: $offset) {
	  	    ...ContentItemsSelect
	  	    __typename
	  	}
		__typename
	  }
	}
	${contentListItemSelectFragment}
`;

export const getWatchListUncategorized = async () => {
    const {
        data: { watchList }
    }: QGLResponse = await api("graphql", {
        method: "POST",
        body: { query: getWatchListUncategorizedQuery, variables: { limit: 1000, offset: 0 } }
    });

    return watchList.itemsUncategorized;
};

export const getStream = async (id: string) => {
    const { data, errors }: QGLResponse = await api("graphql", {
        method: "POST",
        body: { query: getStreamQuery, variables: { id: id } }
    });

    if (errors && errors[0]?.message === "Unauthorized") {
        throw new Error("401");
    }

    return data.stream;
};

export const getContentByCategory = async (
    categoryId: string | number,
    offset: number = 0,
    limit = 15,
    filters?: {
        genres: string[];
        languages: string[];
        countries: string[];
    },
    signal?: AbortSignal
): Promise<CategoryModel> => {
    const response: QGLResponse = await api("graphql", {
        signal,
        method: "POST",
        body: {
            query: `
                query GetCategory($id: String!, $limit: Int!, $offset: Int!, $includeCollections: Boolean, 
                                    $genres: String, $languages: String, $countries: String) {
                  category(id: $id) {
                      id,
                      name
                      items (limit: $limit, offset: $offset, includeCollections: $includeCollections,
                            genres: $genres, languages: $languages, countries: $countries
                            ) {
                          ...ContentItemsSelect
                      }
                  }
                },
                ${contentListItemSelectFragment}
            `,
            variables: {
                id: categoryId,
                offset: offset,
                limit: limit,
                includeCollections: true,
                genres: filters?.genres?.length ? filters.genres.join(",") : null,
                languages: filters?.languages?.length ? filters.languages.join(",") : null,
                countries: filters?.countries?.length ? filters.countries.join(",") : null
            }
        }
    });

    // Handle case where data might be null due to errors
    // Type guard: check if response has errors (GQLErrorData) or valid data (GQLData)
    if ('errors' in response && response.errors) {
        throw new Error(`GraphQL error: ${response.errors[0]?.message || 'Unknown error'}`);
    }

    if (!('data' in response) || !response.data || !response.data.category) {
        throw new Error(`Failed to load category: ${categoryId}`);
    }

    return response.data.category;
};

export const getContentByParentId = async (
    parentId: string | number,
    seriesId: string | number | undefined,
    offset: number = 0,
    limit = 15,
    signal?: AbortSignal
) => {
    const {
        data: { contentItemsByParentId }
    }: QGLResponse = await api("graphql", {
        signal,
        method: "POST",
        body: {
            query: `
                query GetContentItemsByParentId($parentId: String!, $seriesId: String, $limit: Int!, $offset: Int!) {
                  contentItemsByParentId(parentId: $parentId, seriesId: $seriesId, limit: $limit, offset: $offset) {
                     ...ContentItemsSelect
                  }
                },
                ${contentListItemSelectFragment}
            `,
            variables: { parentId: parentId, seriesId, offset: offset, limit: limit, includeCollections: true }
        }
    });

    return contentItemsByParentId;
};

export const getContinueWatching = async (signal?: AbortSignal) => {
    const {
        data: { continueWatching }
    }: QGLResponse = await api("graphql", {
        signal,
        method: "POST",
        body: {
            query: `
                query GetContinueWatching {
                  continueWatching {
                      items {
                        ...ContentItemsSelect
                      }
                  }
                },
            ${contentListItemSelectFragment}

            `
        }
    });

    return continueWatching.items;
};

export const getTrending = async (signal: AbortSignal | undefined = undefined, offset = 0, limit = 25) => {
    const {
        data: { trendingList }
    }: QGLResponse = await api("graphql", {
        signal,
        method: "POST",
        body: {
            query: `
                query GetTrending($includeCollections: Boolean, $limit: Int, $offset: Int) {
                  trendingList {
                      items(includeCollections: $includeCollections, limit: $limit, offset: $offset) {
                        ...ContentItemsSelect
                      }
                  }
                },
            ${contentListItemSelectFragment}
            `,
            variables: { includeCollections: true, offset, limit }
        }
    });

    return trendingList.items;
};

export const search = async ({ query, limit, offset }: ApiParams, signal: any) => {
    const {
        data: { search }
    }: QGLResponse = await api("graphql", {
        signal,
        method: "POST",
        body: {
            query: `
                  query Search ($search: String, $limit: Int, $offset: Int, $includeCollections: Boolean, $includeEpisodes: Boolean){
                      search {
                        id
                        items (search: $search, limit: $limit, offset: $offset, includeCollections: $includeCollections, includeEpisodes: $includeEpisodes) {
                          ...on ContentGroup {
                            id
                            items {
                              ...on TvSeries {
                                id
                                title
                                coverUrl
                                isFree
                              __typename
                              }
                              ...on Episode {
                                id
                                title
                                coverUrl
                                isFree
                                fsk
                                isAdult
                              __typename
                              }
                               ...on ChannelContent {
                                id
                                title
                                coverUrl
                                url
                                logo
                              __typename
                              }
                               ...on Movie {
                                id
                                title
                                coverUrl
                                isFree
                                fsk
                                isAdult
                              __typename
                              }
                              ...on Collection {
                                id
                                title
                                coverUrl
                                isFree
                              __typename
                              }
                              ...on Season {
                                id
                                title
                                coverUrl
                              __typename
                              }
                              ...on LiveStream {
                                id
                                title
                                coverUrl
                                isFree
                              __typename
                              }
                            }
                          }
                        }
                      }
                  }
            `,
            variables: {
                search: query,
                limit,
                offset,
                includeCollections: true,
                includeEpisodes: configs.search?.includeEpisodes || false
            }
        }
    });

    return search.items;
};

export const searchUncategorized = async ({ query, limit, offset }: ApiParams, signal: any) => {
    const {
        data: { search }
    }: QGLResponse = await api("graphql", {
        signal,
        method: "POST",
        body: {
            query: `
                  query Search ($search: String, $limit: Int, $offset: Int, $includeCollections: Boolean, $includeEpisodes: Boolean){
                      search {
                        id
                        itemsUncategorized (search: $search, limit: $limit, offset: $offset, includeCollections: $includeCollections, includeEpisodes: $includeEpisodes) {
                          ...on TvSeries {
                                id
                                title
                                coverUrl
                                isFree
                              __typename
                              }
                              ...on Episode {
                                id
                                title
                                coverUrl
                                isFree
                                fsk
                                isAdult
                              __typename
                              }
                               ...on ChannelContent {
                                id
                                title
                                coverUrl
                                url
                                logo
                              __typename
                              }
                               ...on Movie {
                                id
                                title
                                coverUrl
                                isFree
                                fsk
                                isAdult
                              __typename
                              }
                              ...on Collection {
                                id
                                title
                                coverUrl
                                isFree
                              __typename
                              }
                              ...on Season {
                                id
                                title
                                coverUrl
                              __typename
                              }
                              ...on LiveStream {
                                id
                                title
                                coverUrl
                                isFree
                              __typename
                              }
                          
                        }
                      }
                  }
            `,
            variables: {
                search: query,
                limit,
                offset,
                includeCollections: true,
                includeEpisodes: configs.search?.includeEpisodes || false
            }
        }
    });

    return search.itemsUncategorized;
};

export const getUserData = async () => {
    const { data }: QGLResponse = await api("graphql", {
        method: "POST",
        headers: {
            "x-app-init": "true"
        },
        body: {
            query: `
                query GetUser {
                  user {
                    ...UserData
                  }
                },
                ${userFragment}
            `
        }
    });

    return data.user;
};

export const getUserStatus = async (otp?: string) => {
    const { data }: QGLResponse = await api("graphql", {
        method: "POST",
        body: {
            query: `
                query GetUserStatus($otp: String) {
                  status(otp: $otp) {
                    ...UserData
                  }
                },
                ${userFragment}
            `,
            variables: { otp }
        }
    });

    return data.status;
};

export const getSubscriptionData = async () => {
    const { data }: QGLResponse = await api("graphql", {
        method: "POST",
        body: {
            query: `
                query GetSubscriptionItem {
                  subscriptionsList{
                    price
                    term
                    name
                    relatedSubscriptions {
                        price
                        name
                    }
                    prices {
                        id
                        name
                        term
                        price
                    }
                  }
                }
            `
        }
    });

    return data.subscriptionsList;
};

export const getAvailableFilters = async () => {
    const { data }: QGLResponse = await api("graphql", {
        method: "POST",
        body: {
            query: `
                query GetMetadata {
                  vodMeta{
                    genres  {
                        key
                    }
                    languages  {
                        key
                    }
                  }
                }
            `
        }
    });

    return data.vodMeta;
};

// Batched GraphQL query to fetch subscription data and filters in a single request
export const getSubscriptionDataAndFilters = async () => {
    const { data }: QGLResponse = await api("graphql", {
        method: "POST",
        body: {
            query: `
                query GetSubscriptionAndFilters {
                  subscriptionsList{
                    price
                    name
                    relatedSubscriptions {
                        price
                        name
                    }
                    prices {
                        id
                        name
                        price
                    }
                  }
                  vodMeta{
                    genres  {
                        key
                    }
                    languages  {
                        key
                    }
                  }
                }
            `
        }
    });

    return {
        subscriptionsList: data.subscriptionsList,
        vodMeta: data.vodMeta
    };
};

export const getLiveTvChannelsQuery = `
  query GetLiveTvChannels($offset: Float!, $limit: Float!, $categoryIds: [String!]) {
    getLiveTvChannels(offset: $offset, limit: $limit, categoryIds: $categoryIds) {
      __typename
      id
      name
      format
      tvg_id
      url
      is_active
      is_favourite
      logo
      category_id
      programs {
        id
        start
        stop
        title
      }
    }
  }
`;

export const getLiveTvChannels = async (offset: number = 0, limit: number = 1000, categoryIds?: string[]) => {
    const { data }: QGLResponse = await api("graphql", {
        method: "POST",
        body: {
            query: getLiveTvChannelsQuery,
            variables: { offset, limit, categoryIds }
        }
    });

    return data.getLiveTvChannels;
};

export const getLiveTvGroupsQuery = `
  query GetLiveTvGroups($offset: Float, $limit: Float) {
    getLiveTvGroups(offset: $offset, limit: $limit) {
      name
      channels {
        __typename
        id
        name
        format
        tvg_id
        url
        is_active
        is_favourite
        logo
        category_id
        programs {
          id
          start
          stop
          title
        }
      }
    }
  }
`;

export const getLiveTvGroups = async (offset: number = 0, limit: number = 1000) => {
    const { data }: QGLResponse = await api("graphql", {
        method: "POST",
        body: {
            query: getLiveTvGroupsQuery,
            variables: { offset, limit }
        }
    });

    return data.getLiveTvGroups;
};
